import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  getAdminWebhookVisibilityGetAdminWebhooksEventsIdGuidQueryKey,
  getAdminWebhookVisibilityGetAdminWebhooksEventsQueryKey,
  useAdminWebhookVisibilityGetAdminWebhooksEvents,
  useAdminWebhookVisibilityGetAdminWebhooksEventsIdGuid,
  useAdminWebhookVisibilityGetAdminWebhooksEventsIdGuidApiRequests,
  useAdminWebhookVisibilityPostAdminWebhooksDeliveriesIdGuidReplay,
  useAdminWebhookVisibilityPostAdminWebhooksEventsIdGuidReplayFailed,
} from '@/lib/api/admin-webhook-visibility/admin-webhook-visibility';
import type {
  WebhookEventDetailDto,
  WebhookEventSummaryDto,
} from '@/lib/api/schemas';
import { useHasRole } from '@/lib/auth/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DeliverySummaryPips } from '@/components/webhooks/DeliverySummaryPips';
import { WebhookEventDetailPanel } from '@/components/webhooks/WebhookEventDetailPanel';
import {
  WebhookFiltersBar,
  type WebhookFilters,
} from '@/components/webhooks/WebhookFiltersBar';
import { WebhookStatusBadge } from '@/components/webhooks/WebhookStatusBadge';

export default function WebhooksPage() {
  const [filters, setFilters] = useState<WebhookFilters>({});
  const [pendingFilters, setPendingFilters] = useState<WebhookFilters>({});
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  const query = useAdminWebhookVisibilityGetAdminWebhooksEvents({
    page,
    limit: 25,
    status: filters.status,
    clientId: filters.clientId || undefined,
    merchantId: filters.merchantId || undefined,
    eventCode: filters.eventCode || undefined,
    pspReference: filters.pspReference || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
  });

  const pages = useMemo(() => {
    if (!query.data) return 1;
    const total = Number(query.data.total);
    const limit = Number(query.data.limit) || 1;
    return Math.max(1, Math.ceil(total / limit));
  }, [query.data]);

  const apply = () => {
    setFilters(pendingFilters);
    setPage(1);
  };
  const reset = () => {
    setFilters({});
    setPendingFilters({});
    setPage(1);
  };

  return (
    <section className="space-y-5">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Webhooks</h1>
          <p className="text-sm text-muted-foreground">
            Inbound Adyen events and their per-endpoint dispatcher state. Replay failed deliveries from
            here.
          </p>
        </div>
      </header>

      <WebhookFiltersBar
        pending={pendingFilters}
        onChange={setPendingFilters}
        onApply={apply}
        onReset={reset}
        resultCount={query.data?.total}
      />

      <Card>
        <CardContent className="p-0">
          {query.isError ? (
            <div className="p-6 text-sm text-destructive">
              {(query.error as Error)?.message ?? 'Could not load webhook events.'}
            </div>
          ) : query.isLoading ? (
            <div className="space-y-2 p-6">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="h-10 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : !query.data || query.data.items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No webhook events match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto motion-safe:animate-in motion-safe:fade-in motion-safe:duration-150">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="w-10 p-3"></th>
                    <th className="p-3 font-medium">Time</th>
                    <th className="p-3 font-medium">Client / Merchant</th>
                    <th className="p-3 font-medium">Event</th>
                    <th className="p-3 font-medium">PSP ref</th>
                    <th className="p-3 font-medium">Status</th>
                    <th className="p-3 font-medium">Deliveries</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {query.data.items.map((evt) => (
                    <Fragment key={evt.id}>
                      <Row
                        evt={evt}
                        expanded={expanded === evt.id}
                        onToggle={() =>
                          setExpanded((prev) => (prev === evt.id ? null : evt.id))
                        }
                      />
                      {expanded === evt.id && (
                        <tr>
                          <td colSpan={7} className="bg-muted/20 p-4">
                            <div className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:duration-150">
                              <ExpandedDetailLoader eventId={evt.id} listParams={filters} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        {query.data && query.data.total > query.data.limit && (
          <div className="flex items-center justify-between border-t border-border p-3 text-xs text-muted-foreground">
            <span>
              Page {page} of {pages} · {query.data.total} total
            </span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                Previous
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}>
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </section>
  );
}

function Row({
  evt,
  expanded,
  onToggle,
}: {
  evt: WebhookEventSummaryDto;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <tr className="cursor-pointer hover:bg-muted/30" onClick={onToggle}>
      <td className="p-3 align-top text-muted-foreground">
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </td>
      <td className="p-3 align-top text-xs tabular-nums">{formatTime(evt.createdAt)}</td>
      <td className="p-3 align-top">
        <div className="font-medium">{evt.clientName}</div>
        <div className="font-mono text-xs text-muted-foreground">{evt.merchantCode}</div>
      </td>
      <td className="p-3 align-top">
        <div className="font-mono text-xs">{evt.eventCode}</div>
        <div className="text-[11px] text-muted-foreground">
          {evt.success ? 'success=true' : 'success=false'}
        </div>
      </td>
      <td className="p-3 align-top font-mono text-xs text-muted-foreground">
        {evt.pspReference ?? '—'}
      </td>
      <td className="p-3 align-top">
        <WebhookStatusBadge kind="event" status={evt.status} />
      </td>
      <td className="p-3 align-top">
        <DeliverySummaryPips
          total={evt.deliveryCount}
          delivered={evt.deliveredCount}
          failed={evt.failedCount}
          abandoned={evt.abandonedCount}
        />
      </td>
    </tr>
  );
}

function ExpandedDetailLoader({
  eventId,
  listParams,
}: {
  eventId: string;
  listParams: WebhookFilters;
}) {
  const isAdmin = useHasRole('Admin');
  const queryClient = useQueryClient();

  const detailQuery = useAdminWebhookVisibilityGetAdminWebhooksEventsIdGuid(eventId);
  // Side query for the "Originating API calls" linkage section. Independent of
  // detailQuery so its load state doesn't block the rest of the panel.
  const linkageQuery = useAdminWebhookVisibilityGetAdminWebhooksEventsIdGuidApiRequests(eventId);

  const [recentlyReplayed, setRecentlyReplayed] = useState<Set<string>>(new Set());

  const flashReplay = (deliveryId: string) => {
    setRecentlyReplayed((prev) => {
      const next = new Set(prev);
      next.add(deliveryId);
      return next;
    });
    window.setTimeout(() => {
      setRecentlyReplayed((prev) => {
        const next = new Set(prev);
        next.delete(deliveryId);
        return next;
      });
    }, 1200);
  };

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: getAdminWebhookVisibilityGetAdminWebhooksEventsIdGuidQueryKey(eventId),
    });
    // Refresh whichever filtered list page we came from too.
    void queryClient.invalidateQueries({
      queryKey: getAdminWebhookVisibilityGetAdminWebhooksEventsQueryKey(),
      // partial match — every cached list page invalidates
      exact: false,
    });
    // Reference listParams so eslint doesn't complain about unused arg; the
    // explicit invalidate-all-list-pages above is intentional because the user
    // may have navigated pages.
    void listParams;
  };

  const replayOne = useAdminWebhookVisibilityPostAdminWebhooksDeliveriesIdGuidReplay({
    mutation: {
      onSuccess: (_data, vars) => {
        toast.success('Delivery re-queued for the dispatcher.');
        flashReplay(vars.id);
        invalidate();
      },
      onError: (e) => {
        const msg = (e as Error)?.message ?? 'Could not replay delivery.';
        toast.error(msg);
      },
    },
  });

  const replayAll = useAdminWebhookVisibilityPostAdminWebhooksEventsIdGuidReplayFailed({
    mutation: {
      onSuccess: () => {
        toast.success('All Failed/Abandoned deliveries re-queued.');
        invalidate();
      },
      onError: (e) => {
        const msg = (e as Error)?.message ?? 'Could not replay deliveries.';
        toast.error(msg);
      },
    },
  });

  if (detailQuery.isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="h-8 animate-pulse rounded bg-muted/60" />
        ))}
      </div>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="text-xs text-destructive">
        Could not load event detail.
      </div>
    );
  }

  const detail: WebhookEventDetailDto = detailQuery.data;

  return (
    <WebhookEventDetailPanel
      detail={detail}
      canReplay={isAdmin}
      replayDisabledHint="Admin only"
      isReplayingDelivery={replayOne.isPending}
      isReplayingAll={replayAll.isPending}
      recentlyReplayedIds={recentlyReplayed}
      onReplayDelivery={(id) => replayOne.mutate({ id })}
      onReplayAllFailed={() => replayAll.mutate({ id: detail.summary.id })}
      linkedApiRequests={linkageQuery.data}
      isLoadingLinkedApiRequests={linkageQuery.isLoading}
      buildTransactionHref={(id) => `/transactions/${id}`}
    />
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString([], { dateStyle: 'short', timeStyle: 'medium' });
}
